/**
 * Pick the Sizing — bank-style quiz, walks through 12 questions.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  PICK_SIZING_BANK,
  type PickSizingQuestion,
} from "@/features/quiz/lib/pickSizingQuiz";

export function QuizPickSizing() {
  const {
    question,
    revealedAnswer,
    isCorrect,
    correctAnswer,
    stats,
    submit,
    next,
    bankPosition,
    bankSize,
    isComplete,
    restart,
  } = useQuizSession<PickSizingQuestion, string>({
    quizType: "pick_sizing",
    bank: PICK_SIZING_BANK,
    getCorrectAnswer: (q) => q.options[q.correctIndex]!,
    getQuestionPayload: (q) => ({ prompt: q.prompt }),
  });

  return (
    <QuizLayout
      Icon={Brain}
      title="Pick the Sizing"
      subtitle="Open / 3-bet / 4-bet rules from sheet 1"
      description="Tests the standard preflop sizing rules. Bad sizing leaks money two ways: under-sizing fails to thin the field or extract value, and over-sizing telegraphs hand strength. The questions cover open sizes (live vs online, limper adjustments), 3-bet sizing (IP vs OOP), 4-bet sizing, and stack-depth shifts."
      stats={stats}
      bankPosition={bankPosition}
      bankSize={bankSize}
      isComplete={isComplete}
      onRestart={restart}
    >
      <p className="text-center text-text text-base leading-relaxed">
        {question.prompt}
      </p>

      <div className="grid grid-cols-1 gap-3">
        {question.options.map((opt) => (
          <QuizOption
            key={opt}
            value={opt}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            <span className="block text-left">{opt}</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{correctAnswer}</strong>
            </>
          }
          rationale={question.rationale}
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
